import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Typography, ASAPIcon, ScheduleIcon } from '@qlub-dev/qlub-kit';
import { useTranslation } from '@clubpay/customer-common-module/src/hook/translations';
import { useQlubRouter } from '@clubpay/customer-common-module/src/hook/router';
import { useVendor } from '@clubpay/customer-common-module/src/context/vendor';
import { DiningOptionEnum } from '@clubpay/customer-common-module/src/repository/vendor/type';
import { checkIsRestaurantOpen } from '@/services/utils/k_date';
import { ScheduledOrderEnum } from '@/components/QSR/QSRScheduledOrder';

import styles from './index.module.scss';

interface IProps {
    inline?: boolean;
    diningOption: DiningOptionEnum | undefined;
    value: ScheduledOrderEnum | undefined;
    onChange: (val: ScheduledOrderEnum) => void;
}

const QSRPickUpOptions = ({ inline, diningOption, value, onChange }: IProps) => {
    const { t } = useTranslation('common');
    const { vendor } = useVendor();
    const { lang } = useQlubRouter();

    const isPickupOnly = vendor?.orderConfig?.pickupOnly;

    const options = useMemo(() => {
        return [
            { val: ScheduledOrderEnum.ASAP, icon: <ASAPIcon /> },
            {
                val: ScheduledOrderEnum.Scheduled,
                icon: <ScheduleIcon />,
            },
        ];
    }, []);

    const pickupTitleTranslation = useCallback(
        (val: ScheduledOrderEnum) => {
            switch (val) {
                case ScheduledOrderEnum.ASAP:
                    return t('ASAP');
                case ScheduledOrderEnum.Scheduled:
                    return t('Schedule pickup');
                default:
                    return t('Unknown');
            }
        },
        [lang, t],
    );

    const pickupDescriptionTranslation = useCallback(
        (val: ScheduledOrderEnum) => {
            switch (val) {
                case ScheduledOrderEnum.ASAP:
                    return `${t('Your order will be ready as soon as possible')}. ${t('Pick-up at {{name}}', {
                        name: vendor?.name || '',
                    })}`;
                case ScheduledOrderEnum.Scheduled:
                    return t('Set a time and date to pickup your order');
                default:
                    return t('Unknown');
            }
        },
        [lang, t, vendor?.name],
    );

    const vendorIsOpen = useMemo(() => {
        if (!vendor?.orderConfig?.scheduledOrderEnable || !vendor.config?.openingHour || !vendor.config?.closingHour) {
            return false;
        }

        return checkIsRestaurantOpen(vendor.config?.openingHour, vendor.config?.closingHour);
    }, [vendor]);

    if (inline) {
        if (!vendorIsOpen || isPickupOnly) {
            return null;
        }

        return (
            <div className={styles.inline}>
                <Typography typo="title_md" className={styles.title}>
                    {t('Select a pickup method')}
                </Typography>
                <ToggleButtonGroup
                    className={styles.buttons}
                    fullWidth
                    color="primary"
                    value={value}
                    exclusive
                    onChange={(_, val) => {
                        if (val === null) {
                            return;
                        }
                        onChange(val);
                    }}
                    style={{ direction: 'ltr' }}
                >
                    {options.map((item) => (
                        <ToggleButton
                            key={item.val}
                            value={item.val}
                            sx={{
                                borderRadius: '8px',
                                padding: '10px 12px',
                            }}
                        >
                            <Typography typo="button">{pickupTitleTranslation(item.val)}</Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>{t('Select a pickup method')}</div>
            {options.map((item) => {
                if (item.val === ScheduledOrderEnum.ASAP) {
                    if (!vendorIsOpen) {
                        if (diningOption === DiningOptionEnum.DineIn) {
                            return (
                                <div className={styles.warning} key="warning">
                                    <div className={styles.title}>{t('🥲 Our restaurant is currently closed')}</div>
                                    <div className={styles.desc}>
                                        {t(
                                            "However, we're happy to accept scheduled orders for future pickup or delivery",
                                        )}
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    }
                    if (isPickupOnly) {
                        return null;
                    }
                }
                return (
                    <div
                        key={item.val}
                        onClick={() => {
                            onChange(item.val);
                        }}
                        className={classNames(styles.btn)}
                    >
                        {item.icon}
                        <div className={styles.text}>
                            <Typography className={styles.title}>{pickupTitleTranslation(item.val)}</Typography>
                            <Typography className={styles.desc}>{pickupDescriptionTranslation(item.val)}</Typography>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default QSRPickUpOptions;
